import { useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button as CommonButton,
  ButtonProps,
  IconUserPlus,
  useDeviceDetection,
} from '@gafe/common-ui';
import merge from 'classnames';
import { last } from 'lodash';

import { Button } from '@/components/generic/Button';
import { Isotype } from '@/components/layout/Header/Isotype';
import { Navigation } from '@/components/layout/Header/Navigation/Navigation';
import { useAuth } from '@/hooks/useAuth/useAuth';
import { ROUTES } from '@/router';

import { ROUTES_WITH_EMPTY_HEADER } from './constants';
import { SideBar } from './SideBar';
import { Props } from './types';

import styles from './Header.module.scss';

export const Header = (props: Props) => {
  const history = useHistory();
  const location = useLocation();

  const device = useDeviceDetection();

  const isDesktop = device === 'desktop';

  const isTablet = device === 'tablet';

  const isDashboardPage = /^\/dashboard(\/)?(\?.*)*$/.test(location.pathname);
  const isDetailsPage = /^\/dashboard\/(\d)-([\d\w]+)$/.test(location.pathname);

  const { className, clearGlobalError, blockInteractions } = props;
  const { isAuthenticated, userInfo, requestInProgress, loading } = useAuth();
  const [isOpened, setIsOpened] = useState(false);
  const visible = !(
    loading ||
    requestInProgress ||
    location.pathname.includes(ROUTES.Auth.CreateAccount) ||
    ROUTES_WITH_EMPTY_HEADER.includes(location.pathname)
  );

  const logoRoute = isAuthenticated ? ROUTES.Private.Dashboard.ActivityList : ROUTES.Auth.Welcome;

  const onNavBarActionButtonClick = () => {
    if (isAuthenticated) {
      history.push(ROUTES.Auth.Logout);

      return;
    }

    history.push(ROUTES.Auth.LogIn);
  };

  const navigateToNewLeadCreation = () => {
    setIsOpened(false);
    if (allowedToCreateLeads) {
      history.push(ROUTES.Private.Dashboard.Lead.Create);
    }
  };

  let onLinkClick;

  if (!blockInteractions) {
    onLinkClick = () => {
      setIsOpened(false);
      clearGlobalError();
    };
  }

  const allowedToCreateLeads = userInfo?.companyInfo.isServicesEnabled ?? false;
  const actionTitle = isAuthenticated ? 'Log out' : 'Log in';

  const pageWithoutHeader = [
    last(ROUTES.Private.EstimatedSolarBom.split('/')),
    ROUTES.Private.siteSurvey.segment,
  ].find(route => location.pathname.includes(route ?? ''));

  const getCreateLeadsProps = () => {
    const $props: ButtonProps = {
      colorSchema: 'primary',
      disabled: !allowedToCreateLeads,
      onClick: navigateToNewLeadCreation,
      variant: 'contained',
      prefix: <IconUserPlus colorSchema="monochromatic-light" height="md" width="md" />,
    };

    return $props;
  };

  if (pageWithoutHeader) {
    return null;
  }

  return (
    <header className={merge(styles.header, className)} id={Header.containerId}>
      <div className={styles.container}>
        <Link className={styles.logo_link} onClick={onLinkClick} to={logoRoute}>
          <Isotype />
        </Link>

        {(isDashboardPage || isDetailsPage) && (isDesktop || isTablet) && <Navigation />}

        {!blockInteractions &&
          visible &&
          (userInfo ? (
            <div className={styles.actions}>
              <CommonButton {...getCreateLeadsProps()} data-testid="create-new-lead-button">
                {isDesktop && <span className={styles.text}>Create New Lead</span>}
              </CommonButton>
              <SideBar isOpened={isOpened} toggle={() => setIsOpened(!isOpened)} />
            </div>
          ) : (
            <Button color="gray" colorSchema="filled" onClick={onNavBarActionButtonClick}>
              {actionTitle}
            </Button>
          ))}
      </div>
    </header>
  );
};

Header.containerId = 'header-container';
